import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Check, Copy, Loader2, Palette, Plus, Printer, Send, ThumbsDown, ThumbsUp, Trash2 } from "lucide-react";
import { DiscountFields, PresetPicker, discountOf, presetPatch } from "@/components/MenuTools";
import { Button } from "@/components/ui/button";
import { FieldInput } from "@/components/FieldInput";
import { RowsEditor } from "@/components/ExtrasPanel";
import { QuoteBadge } from "@/pages/Quotes";
import { ShareButton, eventPhrase } from "@/components/ShareDialog";
import { AiDraft, CatalogPicker, OnlineQuote } from "@/components/QuoteTools";
import { AvailabilityAlert } from "@/components/AvailabilityAlert";
import { BrochureButton } from "@/components/BrochureButton";
import { PackagesEditor } from "@/components/Packages";
import { api, errMsg, fmtMoney, fmtValue, openPdf } from "@/lib/api";

const Card = ({ title, children, testId }) => (
  <section className="rounded-xl border border-[#D4E6EC] bg-white p-5 sm:p-7 space-y-5 fade-up" data-testid={testId}>
    <h3 className="font-serif-it text-2xl font-semibold">{title}</h3>
    {children}
  </section>
);
const Grid = ({ children }) => <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-5">{children}</div>;

function Actions({ q, setStatus, convert, remove, duplicate }) {
  const s = q.status;
  return (
    <div className="flex flex-wrap gap-2" data-testid="quote-actions">
      {s === "bozza" && <Button variant="outline" className="rounded-full" onClick={() => setStatus("inviato")} data-testid="quote-mark-sent"><Send size={15} className="mr-2" /> Segna come inviato</Button>}
      {["bozza", "inviato", "rifiutato"].includes(s) && <Button className="rounded-full bg-[#2A9D8F] hover:bg-[#21867A]" onClick={() => setStatus("accettato")} data-testid="quote-mark-accepted"><ThumbsUp size={15} className="mr-2" /> Il cliente ha accettato</Button>}
      {["bozza", "inviato"].includes(s) && <Button variant="ghost" className="rounded-full" onClick={() => setStatus("rifiutato")} data-testid="quote-mark-rejected"><ThumbsDown size={15} className="mr-2" /> Rifiutato</Button>}
      {s === "accettato" && <Button className="rounded-full" onClick={convert} data-testid="quote-convert-button"><ArrowRight size={15} className="mr-2" /> Trasforma in contratto</Button>}
      {s === "accettato" && <Button variant="ghost" className="rounded-full" onClick={() => setStatus("inviato")} data-testid="quote-undo-accept">Annulla accettazione</Button>}
      <Button variant="outline" className="rounded-full" onClick={duplicate} data-testid="quote-duplicate"><Copy size={15} className="mr-2" /> Duplica per nuovo cliente</Button>
      {s === "bozza" && <Button variant="ghost" className="rounded-full text-[#991B1B]" onClick={remove} data-testid="quote-delete"><Trash2 size={15} className="mr-2" /> Elimina</Button>}
    </div>
  );
}

export default function QuoteEditor() {
  const { id } = useParams();
  const nav = useNavigate();
  const [q, setQ] = useState(null);
  const [tpl, setTpl] = useState(null);
  const [saveState, setSaveState] = useState("saved");
  const timer = useRef(null);
  const latest = useRef(null);

  const load = useCallback(async () => {
    const r = await api.get(`/quotes/${id}`);
    setQ(r.data);
    latest.current = r.data;
  }, [id]);
  useEffect(() => {
    api.get("/quote-template").then((r) => setTpl(r.data));
    load().catch((e) => toast.error(errMsg(e)));
  }, [load]);

  const flush = async () => {
    clearTimeout(timer.current);
    if (!timer.current) return;
    timer.current = null;
    const cur = latest.current;
    const r = await api.put(`/quotes/${id}`, { evento_tipo: cur.evento_tipo, data: cur.data, items: cur.items });
    setQ((p) => ({ ...p, totals: r.data.totals }));
    setSaveState("saved");
  };
  const change = (patch) => {
    setQ((prev) => {
      const next = { ...prev, ...patch };
      latest.current = next;
      return next;
    });
    setSaveState("dirty");
    clearTimeout(timer.current);
    timer.current = setTimeout(() => flush().catch((e) => { setSaveState("error"); toast.error(errMsg(e)); }), 800);
  };

  if (!q || !tpl) return <div className="text-sm text-[#7B929C]">Caricamento…</div>;
  const editable = !["accettato", "convertito", "annullato"].includes(q.status);
  const spec = q.evento_tipo ? tpl.types[q.evento_tipo] : null;
  const setData = (k, v) => change({ data: { ...q.data, [k]: v } });
  const n = (v) => Number(v || 0);
  const total = n(q.data.adulti) * n(q.data.prezzo_adulti) + n(q.data.bambini) * n(q.data.prezzo_bambini) + (q.items || []).reduce((t, r) => t + n(r.quantita) * n(r.unitario), 0);
  const sconto = discountOf(total, q.data.sconto_tipo, q.data.sconto_valore);

  const act = async (fn, msg) => {
    try { await flush(); await fn(); toast.success(msg); await load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const setStatus = (status) => act(() => api.post(`/quotes/${id}/status`, { status }), "Stato aggiornato");
  const convert = async () => {
    try {
      const r = await api.post(`/quotes/${id}/convert`);
      toast.success(`Creato il contratto ${r.data.contract_number}`);
      nav(`/contratti/${r.data.contract_id}`);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const remove = () => act(async () => { await api.delete(`/quotes/${id}`); nav("/preventivi"); }, "Preventivo eliminato");
  const duplicate = async () => {
    try {
      await flush();
      const r = await api.post(`/quotes/${id}/duplicate`);
      toast.success("Preventivo duplicato: inserisci il nuovo cliente");
      nav(`/preventivi/${r.data.id}`);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const addSuggested = (s) => change({ items: [...(q.items || []), { servizio: s, quantita: 1, unitario: 0 }] });

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 fade-up">
        <div>
          <Link to="/preventivi" className="text-xs text-[#7B929C] inline-flex items-center gap-1 hover:text-[#0B6E8F]" data-testid="back-to-quotes"><ArrowLeft size={13} /> Tutti i preventivi</Link>
          <div className="flex items-center gap-3 mt-2 flex-wrap">
            <h1 className="font-serif-it text-4xl sm:text-5xl font-semibold tracking-tight" data-testid="quote-title">{q.data.cliente_nome || "Nuovo preventivo"}</h1>
            <QuoteBadge status={q.status} testId="quote-status" />
          </div>
          <div className="text-sm text-[#47606B] mt-1"><span className="font-mono-it text-xs" data-testid="quote-number">{q.number}</span> • {q.evento_tipo || "tipo evento da scegliere"}</div>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs text-[#7B929C] inline-flex items-center gap-1" data-testid="quote-save-indicator">{saveState === "dirty" ? <><Loader2 size={13} className="animate-spin" /> Salvataggio…</> : saveState === "error" ? "Errore di salvataggio" : <><Check size={13} /> Salvato</>}</span>
          <Button variant="outline" className="rounded-full" onClick={async () => { await flush(); openPdf(`/quotes/${id}/pdf`); }} data-testid="quote-pdf-button"><Printer size={15} className="mr-2" /> PDF preventivo</Button>
          {q.evento_tipo && (
            <ShareButton testId="quote-share-button" doc={{ kind: "quote", id }} phone={q.data.cliente_tel} fileName={`Preventivo-${q.number}.pdf`}
              body={`🌟 Gentile *${q.data.cliente_nome || "Cliente"}*,\n\n🙏 la ringraziamo per averci scelto! Le inviamo il *preventivo n. ${q.number}* per ${eventPhrase(q.evento_tipo)}${q.data.evento_data ? ` del 📅 *${fmtValue({ type: "date" }, q.data.evento_data)}*` : ""}, preparato con cura sulla base delle sue richieste. 🍽️✨\n\n💬 Restiamo a completa disposizione per qualsiasi chiarimento o personalizzazione.\n\nUn cordiale saluto 🌊`} />
          )}
        </div>
      </div>

      {["convertito", "annullato"].includes(q.status) && (
        <div className={`rounded-xl border p-4 text-sm flex items-center justify-between gap-3 flex-wrap ${q.status === "annullato" ? "bg-[#FEF2F2] border-[#FECACA] text-[#991B1B]" : "bg-[#F0FDF4] border-[#BBF7D0]"}`} data-testid="quote-converted-box">
          {q.status === "annullato" ? <span>Il contratto <b>{q.contract_number}</b> nato da questo preventivo è stato annullato{q.cancel_reason ? `: ${q.cancel_reason}` : ""}. Per riproporlo, duplica il preventivo.</span> : <span>Preventivo trasformato nel contratto <b>{q.contract_number}</b>.</span>}
          <Button size="sm" className="rounded-full" onClick={() => nav(`/contratti/${q.contract_id}`)} data-testid="quote-open-contract">Apri contratto</Button>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
        <div className="lg:col-span-8 space-y-6">
          {editable && <AiDraft q={q} change={change} />}
          <Card title="1. Tipo di evento" testId="quote-type-card">
            <div className="flex flex-wrap gap-2">
              {Object.keys(tpl.types).map((t, i) => (
                <button key={t} disabled={!editable} data-testid={`quote-type-opt-${i}`} onClick={() => change({ evento_tipo: t, data: { ...q.data, a_formula: q.data.a_formula || tpl.types[t].formula } })}
                  className={`px-5 py-2.5 rounded-full border text-sm transition-colors ${q.evento_tipo === t ? "bg-[#0B6E8F] text-white border-[#0B6E8F]" : "bg-white border-[#D4E6EC] hover:border-[#0B6E8F]"}`}>{t}</button>
              ))}
            </div>
          </Card>
          {spec && (
            <>
              <Card title="2. Cliente ed evento" testId="quote-client-card">
                <Grid>
                  {[...tpl.common.filter((f) => !["adulti", "bambini", "prezzo_adulti", "prezzo_bambini"].includes(f.k)), ...spec.fields].map((f) => (
                    <FieldInput key={f.k} f={f} value={q.data[f.k]} onChange={(v) => setData(f.k, v)} disabled={!editable} />
                  ))}
                </Grid>
                <AvailabilityAlert date={q.data.evento_data} sede={q.data.sede} quoteId={id} />
              </Card>
              <Card title="3. Menù proposto" testId="quote-menu-card">
                {editable && <PresetPicker evento={q.evento_tipo} onApply={(p) => { change({ data: { ...q.data, ...presetPatch(p) } }); toast.success(`${p.nome} caricato`); }} />}
                <Grid>
                  <FieldInput f={{ k: "a_formula", label: "Formula", type: "select", options: tpl.formule }} value={q.data.a_formula} onChange={(v) => setData("a_formula", v)} disabled={!editable} />
                  <div />
                  {spec.menu.map(([k, label]) => <FieldInput key={k} f={{ k, label, type: "textarea" }} value={q.data[k]} onChange={(v) => setData(k, v)} disabled={!editable} />)}
                </Grid>
              </Card>
              <Card title="4. Ospiti, prezzi e servizi" testId="quote-prices-card">
                <Grid>
                  {tpl.common.filter((f) => ["adulti", "prezzo_adulti", "bambini", "prezzo_bambini"].includes(f.k)).map((f) => (
                    <FieldInput key={f.k} f={f} value={q.data[f.k]} onChange={(v) => setData(f.k, v)} disabled={!editable} />
                  ))}
                </Grid>
                <RowsEditor rows={q.items || []} setRows={(items) => change({ items })} disabled={!editable} prefix="quote" />
                {editable && <CatalogPicker q={q} change={change} />}
                <DiscountFields data={q.data} setData={setData} disabled={!editable} gross={total} />
                {editable && (
                  <div className="flex flex-wrap gap-2 items-center">
                    <span className="text-xs text-[#7B929C]">Servizi tipici per {q.evento_tipo.toLowerCase()}:</span>
                    {spec.suggested.map((s, i) => (
                      <button key={s} onClick={() => addSuggested(s)} data-testid={`quote-suggest-${i}`} className="text-xs px-3 py-1.5 rounded-full border border-dashed border-[#34B3B0] hover:bg-[#EAF5F7] inline-flex items-center gap-1"><Plus size={12} /> {s}</button>
                    ))}
                  </div>
                )}
                <Grid>
                  <FieldInput f={{ k: "validita_giorni", label: "Validità del preventivo (giorni)", type: "number" }} value={q.data.validita_giorni} onChange={(v) => setData("validita_giorni", v)} disabled={!editable} />
                  <FieldInput f={{ k: "note", label: "Note per il cliente", type: "textarea" }} value={q.data.note} onChange={(v) => setData("note", v)} disabled={!editable} />
                </Grid>
              </Card>
              <PackagesEditor q={q} spec={spec} change={change} disabled={!editable} />
            </>
          )}
        </div>
        <aside className="lg:col-span-4 lg:sticky lg:top-24 space-y-4">
          <div className="rounded-xl bg-gradient-to-br from-[#0B6E8F] to-[#0D2B3A] text-white p-6" data-testid="quote-total-box">
            <div className="text-xs uppercase tracking-widest text-white/60">Totale preventivo</div>
            {sconto > 0 && <div className="text-sm text-white/70 mt-2" data-testid="quote-subtotal">Subtotale {fmtMoney(total)} • sconto - {fmtMoney(sconto)}</div>}
            <div className="font-serif-it text-5xl font-semibold mt-2" data-testid="quote-total">{fmtMoney(total - sconto)}</div>
            <div className="text-xs text-white/60 mt-2">{n(q.data.adulti)} adulti • {n(q.data.bambini)} bambini • {(q.items || []).length} servizi</div>
          </div>
          <div className="rounded-xl border border-[#D4E6EC] bg-white p-5 space-y-3">
            <div className="text-sm text-[#47606B]">Quando il cliente accetta, trasforma il preventivo in contratto: tipo evento, cliente, data, sede, ospiti, prezzi, menù e servizi vengono riportati automaticamente.</div>
            <Actions q={q} setStatus={setStatus} convert={convert} remove={remove} duplicate={duplicate} />
            <OnlineQuote q={q} id={id} reload={load} />
            {q.evento_tipo && <BrochureButton source="quote" id={id} phone={q.data.cliente_tel} nome={q.data.cliente_nome} flush={flush} />}
            <Link to={`/menu-artistico/quote/${id}`} onClick={() => flush?.()} className="inline-flex items-center gap-1.5 text-sm text-[#0B6E8F] hover:underline" data-testid="quote-art-link"><Palette size={14} /> Menù artistico da stampare</Link>
          </div>
          {(q.history || []).length > 0 && (
            <div className="rounded-xl border border-[#D4E6EC] bg-white p-5 text-xs space-y-2" data-testid="quote-history">
              {q.history.slice().reverse().map((h, i) => <div key={i}><span className="text-[#7B929C]">{new Date(h.at).toLocaleString("it-IT")}</span> • {h.action} <span className="text-[#7B929C]">({h.by})</span></div>)}
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}
